import {
  DetailRequirement,
  EngagementTaskDateParams,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  SingularTaskTraitDetail,
} from '@cibo/core'
import { Markdown } from '@cibo/ui'
import { Stack } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useDebounceCallback } from '@react-hook/debounce'
import { UseQueryResult } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useProgramEngagementContext } from '../../../../context'
import { TaskDataModel } from '../../models/TaskDataModel'

type DatePickerTraitEditorProps = {
  taskId: string
  traitConfig: EngagementTaskTrait
  taskData: UseQueryResult<TaskDataModel, Error>
  detailRequirement?: DetailRequirement
  resourceId: string
  onChange: (detail: MultipleTaskTraitDetail | SingularTaskTraitDetail) => void
  immutable?: boolean
  traitDetailValue: any
}

export const BACKEND_MIN_DATE = '1920-01-01'
export const BACKEND_MAX_DATE = '2049-12-31'

export const DatePickerTraitEditor = ({
  detailRequirement,
  traitConfig,
  onChange,
  immutable,
  traitDetailValue,
}: DatePickerTraitEditorProps) => {
  const { engagement, taskData } = useProgramEngagementContext()

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params as EngagementTaskDateParams

  const helperText = engagement.data && engagement.data.replaceCopyTokens(params?.helperTextMd)

  const [displayValue, setDisplayValue] = useState<dayjs.Dayjs | Date | null>(
    traitDetailValue ? dayjs(traitDetailValue) : null
  )

  useEffect(() => {
    if (traitDetailValue && !displayValue) setDisplayValue(dayjs(traitDetailValue))
  }, [traitDetailValue])

  const saveNewValue = useDebounceCallback((newValue: string | null) => {
    onChange({
      traitId: traitConfig.traitId,
      value: newValue,
      year: detailRequirement?.year,
    })
  }, 300)

  useEffect(() => {
    const newDateString = displayValue?.toISOString() || null

    if (newDateString !== traitDetailValue) {
      saveNewValue(newDateString)
    }
  }, [displayValue])

  return (
    <Stack spacing={1}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          disabled={immutable}
          minDate={dayjs(params?.minDate || BACKEND_MIN_DATE)}
          maxDate={dayjs(params?.maxDate || BACKEND_MAX_DATE)}
          slotProps={{
            field: { clearable: true, onClear: () => setDisplayValue(null) },
            textField: {
              size: 'small',
              helperText: helperText,
              style: { marginLeft: 0, width: '260px' },
            },
          }}
          value={displayValue}
          onChange={newValue => newValue && setDisplayValue(newValue)}
        />
      </LocalizationProvider>

      {!!helperText && <Markdown typographyProps={{ variant: 'caption' }}>{helperText}</Markdown>}
    </Stack>
  )
}
